<script lang="ts">
    import {getLocalization} from 'src/core';
    import type {Availability, CapabilityName} from 'src/typings';
    import type {AvailabilityInfo, ButtonType} from './types';
    import {exists, isDefined} from 'shared/utils/custom-utils';

    export let availability: Availability;
    export let forExemplar: boolean;
    export let size: 'lg' | 'md' | 'sm' | 'xs' = 'sm';
    export let block: boolean;
    export let downloadUrl: string | null = null;

    const localize = getLocalization();

    let buttonAvailabilityInfo: AvailabilityInfo;
    let buttonType: ButtonType;
    let buttonTypeClasses: string;

    $: if (availability) buttonAvailabilityInfo = getButtonAvailabilityInfo(availability)
    $: if (availability) buttonType = getButtonType(availability);
    $: if (buttonType) buttonTypeClasses = getButtonTypeClasses(buttonType);

    function getCapabilityCountOf(availabilityInfo: Availability, ...capabilityNames: CapabilityName[]): number {
        if (!availabilityInfo || !isDefined(availabilityInfo.capabilities)) {
            return 0;
        }
        return capabilityNames.filter((capabilityName) => availabilityInfo.capabilities.some((cap) => cap.name === capabilityName)).length;
    }

    function isCapableOfOneOf(availabilityInfo: Availability, ...capabilityNames: CapabilityName[]) {
        return getCapabilityCountOf(availabilityInfo, ...capabilityNames) === 1;
    }

    function isCapableOfAnyOf(availabilityInfo: Availability, ...capabilityNames: CapabilityName[]) {
        return getCapabilityCountOf(availabilityInfo, ...capabilityNames) > 0;
    }

    function getButtonAvailabilityInfo(availabilityInfo: Availability): AvailabilityInfo {
        if (isCapableOfAnyOf(availabilityInfo, 'seekingProvidability')) {
            return {label: localize(/* @kp-localization loan.VyzadatPresMvs */ 'loan.VyzadatPresMvs')}
        } else if (availabilityInfo.capabilities.length > 1 && isCapableOfAnyOf(availabilityInfo, 'downloadability')) {
            return {label: localize(/* @kp-localization loan.request.ObtainOptionsIncludingDownload */ 'loan.request.ObtainOptionsIncludingDownload')}
        } else if (availabilityInfo.capabilities.length > 1 && isCapableOfOneOf(availabilityInfo, 'palmknihyAudioLoanability')) {
            return {label: localize(/* @kp-localization loan.request.ObtainOptionsIncludingAudio */ 'loan.request.ObtainOptionsIncludingAudio')}
        } else if (availabilityInfo.capabilities.length > 1 && isCapableOfOneOf(availabilityInfo, 'palmknihyLoanability', 'palmknihyPdfLoanability', 'flexibooksLoanability', 'levnaknihovnaLoanability')) {
            return {label: localize(/* @kp-localization loan.request.ObtainOptionsIncludingEloan */ 'loan.request.ObtainOptionsIncludingEloan')}
        } else if (availabilityInfo.capabilities.length > 1) {
            return {label: localize(/* @kp-localization loan.request.ObtainOptions */ 'loan.request.ObtainOptions')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'mailRequestability')) {
            return {label: localize(/* @kp-localization loan.Objednat */ 'loan.Objednat')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'pickupability')) {
            return {label: localize(/* @kp-localization loan.request.Vypujcit */ 'loan.request.Vypujcit'), dataQa: 'exemplar-available-button'}
        } else if (isCapableOfAnyOf(availabilityInfo, 'standardOrderability')) {
            return {label: localize(/* @kp-localization loan.Objednat */ 'loan.Objednat'), dataQa: 'exemplar-order-button'}
        } else if (isCapableOfAnyOf(availabilityInfo, 'standardReservability')) {
            return {label: localize(/* @kp-localization commons.rezervovat */ 'commons.rezervovat'), dataQa: 'exemplar-reservation-button'}
        } else if (isCapableOfAnyOf(availabilityInfo, 'visitPickupability')) {
            return {label: localize(/* @kp-localization loan.PickupForVisit */ 'loan.PickupForVisit'), dataQa: 'exemplar-free-for-visit-button'}
        } else if (isCapableOfAnyOf(availabilityInfo, 'standardVisitOrderability')) {
            return {label: localize(/* @kp-localization loan.OrderForVisit */ 'loan.OrderForVisit')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'standardVisitReservability')) {
            return {label: localize(/* @kp-localization loan.ReserveForVisit */ 'loan.ReserveForVisit')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'palmknihyLoanability')) {
            return {label: localize(/* @kp-localization loan.palmknihy.Vypujcit */ 'loan.palmknihy.Vypujcit')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'palmknihyAudioLoanability')) {
            return {label: localize(/* @kp-localization loan.palmknihy.BorrowAudiobook */ 'loan.palmknihy.BorrowAudiobook')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'palmknihyPdfLoanability')) {
            return {label: localize(/* @kp-localization loan.palmknihy.BorrowPdf */ 'loan.palmknihy.BorrowPdf')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'flexibooksLoanability')) {
            return {label: localize(/* @kp-localization loan.VypujcitEBook */ 'loan.VypujcitEBook')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'levnaknihovnaLoanability')) {
            return {label: localize(/* @kp-localization loan.VypujcitEBook */ 'loan.VypujcitEBook')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'downloadability')) {
            return {label: localize(/* @kp-localization commons.ZobrazitPlnyText */ 'commons.ZobrazitPlnyText')}
        } else if (isCapableOfAnyOf(availabilityInfo, 'slotVisitOrderability')) {
            return {label: localize(/* @kp-localization loan.OrderOnTime */ 'loan.OrderOnTime')}
        } else {
            return {label: localize(/* @kp-localization loan.CannotRequestLoanForMyself */ 'loan.CannotRequestLoanForMyself'), dataQa: 'exemplar-not-available-button'}
        }
    }

    function getButtonTypeClasses(type: ButtonType): string {
        if (type === 'reservable') {
            return 'available btn-brand-orange';
        } else if (type === 'available') {
            return 'available btn-success';
        } else {
            return 'not-available btn-danger';
        }
    }

    function getButtonType(availabilityInfo: Availability): ButtonType {
        if (availabilityInfo.capabilities.length === 1 && isCapableOfAnyOf(availabilityInfo, 'standardReservability', 'standardVisitReservability')) {
            return 'reservable';
        } else if (availabilityInfo.capabilities.length > 0) {
            return 'available';
        } else {
            return 'not-available';
        }
    }
</script>

{#if availability && availability.reasonable && (!forExemplar || availability.capabilities.length > 0)}
    <div>
        <svelte:element
                this={exists(downloadUrl) ? 'a' : 'button'}
                href={downloadUrl}
                target="_blank"
                role="button"
                tabindex="0"
                class={`btn btn-default availability-button btn-${size} ${buttonTypeClasses}`}
                data-qa={buttonAvailabilityInfo?.dataQa}
                class:btn-block={block}
                class:availability-button-exemplar={forExemplar}
                class:availability-button-document={!forExemplar}
                class:reasonable={availability.reasonable}
                class:not-reasonable={!availability.reasonable}
                class:pickable={isCapableOfAnyOf(availability, 'pickupability', 'visitPickupability')}
                class:not-pickable={!isCapableOfAnyOf(availability,'pickupability', 'visitPickupability')}
                class:orderable={isCapableOfAnyOf(availability,'standardOrderability', 'standardVisitOrderability')}
                class:not-orderable={!isCapableOfAnyOf(availability,'standardOrderability', 'standardVisitOrderability')}
                class:reservable={isCapableOfAnyOf(availability,'standardReservability', 'standardVisitReservability')}
                class:not-reservable={!isCapableOfAnyOf(availability,'standardReservability', 'standardVisitReservability')}
                class:downloadable={isCapableOfAnyOf(availability,'downloadability')}
                class:not-downloadable={!isCapableOfAnyOf(availability,'downloadability')}
                class:mail-requestable={isCapableOfAnyOf(availability,'mailRequestability')}
                class:not-mail-requestable={!isCapableOfAnyOf(availability,'mailRequestability')}
                class:mvs-requestable={isCapableOfAnyOf(availability,'seekingProvidability')}
                class:not-mvs-requestable={!isCapableOfAnyOf(availability,'seekingProvidability')}
                class:palmknihy-loanable={isCapableOfAnyOf(availability,'palmknihyLoanability')}
                class:not-palmknihy-loanable={!isCapableOfAnyOf(availability,'palmknihyLoanability')}
                class:palmknihy-audio-loanable={isCapableOfAnyOf(availability,'palmknihyAudioLoanability')}
                class:not-palmknihy-audio-loanable={!isCapableOfAnyOf(availability,'palmknihyAudioLoanability')}
                class:palmknihy-pdf-loanable={isCapableOfAnyOf(availability,'palmknihyPdfLoanability')}
                class:not-palmknihy-pdf-loanable={!isCapableOfAnyOf(availability,'palmknihyPdfLoanability')}
                class:flexibooks-loanable={isCapableOfAnyOf(availability,'flexibooksLoanability')}
                class:not-flexibooks-loanable={!isCapableOfAnyOf(availability,'flexibooksLoanability')}
                class:levnaknihovna-loanable={isCapableOfAnyOf(availability,'levnaknihovnaLoanability')}
                class:not-levnaknihovna-loanable={!isCapableOfAnyOf(availability,'levnaknihovnaLoanability')}
                on:click>

            <slot label={buttonAvailabilityInfo.label} dataQa={buttonAvailabilityInfo?.dataQa} type={buttonType}>
                <span data-qa={buttonAvailabilityInfo?.dataQa}>{buttonAvailabilityInfo.label}</span>
            </slot>
        </svelte:element>
    </div>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .availability-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: @spacing-s;
        white-space: nowrap;

        &.availability-button-document {
            margin-right: 12px;
        }
    }
</style>